import { useState, useEffect, useCallback } from 'react'
import cutileiApi from 'services/cutileiApi'
import PayoutService from 'services/Payout'
import { logApiError } from 'services/apiError'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  Card,
  CardHeader,
  CardTitle,
  SubTitle,
  SummaryGrid,
  SummaryCell,
  SummaryLabel,
  SummaryValue,
  SummaryCount,
  Column,
  InfoText,
  WarningText
} from '../styles'

/*
  Os totais que o financeiro precisa antes de olhar qualquer lista: quanto está em cada
  situação, quanto é do salão e quanto é da manicure, e quanto a Cutilei retém de antecipação.

  A receita de antecipação fica num bloco próprio porque **esta é a única fonte dela**: a taxa
  é dinheiro que simplesmente não sai da conta mãe, não existe como lançamento em extrato
  nenhum, e não há outro lugar no sistema onde somá-la.
*/
export default function Summary ({ filters, requestConfig }) {
  const [loading, setLoading] = useState (true)
  const [summary, setSummary] = useState (null)
  const [error, setError] = useState (null)

  const { startDate, endDate } = filters ?? {}

  const load = useCallback (async () => {
    try {
      setLoading (true)

      const query = new URLSearchParams ()

      if (startDate) query.set ('start_date', startDate)
      if (endDate) query.set ('end_date', endDate)

      const { data } = await cutileiApi.get (
        `/admin/payouts/summary?${query.toString ()}`, requestConfig
      )

      setSummary (data)
      setError (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar os totais')
    } finally {
      setLoading (false)
    }
  }, [startDate, endDate, requestConfig])

  useEffect (() => {
    load ()
  }, [load])

  if (loading) return (
    <Card>
      <Loading style={{alignSelf: 'center', margin: '10px 0px'}}/>
    </Card>
  )

  if (error) return (
    <Card>
      <CardTitle>Totais</CardTitle>
      <WarningText>{error}</WarningText>
    </Card>
  )

  const byStatus = summary?.byStatus ?? []
  const byUserType = summary?.byUserType ?? []
  const feeTotal = Number (summary?.anticipationFeeTotal ?? 0)
  const feeCount = Number (summary?.anticipationCount ?? 0)

  return (
    <Card gap={16}>
      <CardHeader>
        <CardTitle>Totais</CardTitle>
      </CardHeader>

      {byStatus.length === 0 ? (
        <InfoText>
          {'Nenhum recebível gerado ainda. Verifique se o repasse automático está ligado e se '
          + 'o marco zero foi configurado, na aba de configuração'}
        </InfoText>
      ) : (
        <Column gap={8}>
          <SubTitle>Por situação</SubTitle>
          <SummaryGrid>
            {byStatus.map (row => (
              <SummaryCell key={row.status}>
                <SummaryLabel>{PayoutService.getStatusLabel (row.status)}</SummaryLabel>
                <SummaryValue value={row.total}/>
                <SummaryCount>
                  {`${row.count} ${row.count === 1 ? 'recebível' : 'recebíveis'}`}
                </SummaryCount>
              </SummaryCell>
            ))}
          </SummaryGrid>
        </Column>
      )}

      {byUserType.length > 0 && (
        <Column gap={8}>
          <SubTitle>Por titular</SubTitle>
          <SummaryGrid>
            {byUserType.map (row => (
              <SummaryCell key={row.userType}>
                <SummaryLabel>{PayoutService.getUserTypeLabel (row.userType)}</SummaryLabel>
                <SummaryValue value={row.total}/>
                <SummaryCount>
                  {`${row.count} ${row.count === 1 ? 'recebível' : 'recebíveis'}`}
                </SummaryCount>
              </SummaryCell>
            ))}
          </SummaryGrid>
          <InfoText>Cancelados ficam fora deste recorte</InfoText>
        </Column>
      )}

      <Column gap={8}>
        <SubTitle>Receita de antecipação</SubTitle>
        <SummaryGrid>
          <SummaryCell>
            <SummaryLabel>Retido pela Cutilei</SummaryLabel>
            <SummaryValue value={feeTotal}/>
            <SummaryCount>
              {`${feeCount} ${feeCount === 1 ? 'antecipação' : 'antecipações'}`}
            </SummaryCount>
          </SummaryCell>
        </SummaryGrid>
        <InfoText>
          {'A taxa não aparece em extrato nenhum: é dinheiro que não saiu da conta da '
          + 'Cutilei. Este total é a única fonte dela'}
        </InfoText>
      </Column>
    </Card>
  )
}
